import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  X,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  HelpCircle,
  ShieldCheck,
  ShieldAlert,
  Wrench,
  Ban,
} from 'lucide-react';
import clsx from 'clsx';
import { servers } from '../api';
import type { CheckStatus, FixResult, HostCheck } from '../api';
import { useDateFormat } from '../lib/dates';

/**
 * Does this host meet the conditions we need — before anything is rolled out on
 * it.
 *
 * Exists because the first real base-stack rollout ran into a host with only
 * `docker-compose` 1.x, which creates containers happily and dies on every
 * recreate. That would have surfaced weeks later, during an update on a customer
 * instance. Everything here is a condition whose absence has cost us something or
 * would have.
 */

const STATUS_ICON: Record<CheckStatus, typeof CheckCircle2> = {
  PASS: CheckCircle2,
  WARN: AlertTriangle,
  FAIL: XCircle,
  UNKNOWN: HelpCircle,
};

const STATUS_TONE: Record<CheckStatus, string> = {
  PASS: 'text-emerald-600',
  WARN: 'text-amber-600',
  FAIL: 'text-red-600',
  UNKNOWN: 'text-slate-400',
};

const STATUS_ROW: Record<CheckStatus, string> = {
  PASS: 'border-slate-200 bg-surface',
  WARN: 'border-amber-200 bg-amber-50/60',
  FAIL: 'border-red-200 bg-red-50/60',
  UNKNOWN: 'border-slate-200 bg-slate-50',
};

function CheckRow({
  check,
  serverId,
  onFixed,
}: {
  check: HostCheck;
  serverId: string;
  onFixed: () => void;
}) {
  const Icon = STATUS_ICON[check.status];
  const [result, setResult] = React.useState<FixResult | null>(null);

  const fixMutation = useMutation({
    mutationFn: () => servers.hostFix(serverId, check.fix!.action),
    onSuccess: (fixResult) => {
      setResult(fixResult);
      // Re-measure rather than assume: the button reports what it did, the check
      // reports what is true now, and those are not the same statement. A revoke
      // also has to re-measure — it turns a green row red, and that is the point.
      if (fixResult.ok) onFixed();
    },
    onError: (err: unknown) => {
      const response = (err as { response?: { data?: FixResult & { message?: string } } }).response;
      setResult(
        response?.data?.log !== undefined
          ? response.data
          : {
              ok: false,
              log: '',
              error:
                response?.data?.message ??
                (err instanceof Error ? err.message : 'Maßnahme fehlgeschlagen.'),
            },
      );
    },
  });

  function runFix() {
    const fix = check.fix!;
    const question = [fix.description, fix.caution ? `\nAchtung: ${fix.caution}` : '', '\nAusführen?']
      .filter(Boolean)
      .join('\n');
    if (confirm(question)) fixMutation.mutate();
  }

  return (
    <div className={clsx('rounded-lg border px-3 py-2', STATUS_ROW[check.status])}>
      <div className="flex items-start gap-2.5">
        <Icon size={15} className={clsx('mt-0.5 shrink-0', STATUS_TONE[check.status])} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-medium text-slate-800">{check.label}</span>
            {check.value && (
              <span className="font-mono text-xs text-slate-500">{check.value}</span>
            )}
            {check.blocking && check.status === 'FAIL' && (
              <span className="rounded bg-red-600 px-1.5 py-0.5 text-[11px] font-medium text-white">
                blockiert Deploy
              </span>
            )}
          </div>
          {check.detail && (
            <p className="mt-0.5 text-xs leading-snug text-slate-600">{check.detail}</p>
          )}
        </div>
        {check.fix && (
          <button
            onClick={runFix}
            disabled={fixMutation.isPending}
            title={check.fix.description}
            className={clsx(
              'inline-flex shrink-0 items-center gap-1 rounded-lg border bg-surface px-2.5 py-1.5 text-xs font-medium disabled:opacity-50',
              // A revoke sits on a green row and takes access away. Same shape as
              // the repair button would read as "make this better".
              check.fix.kind === 'revoke'
                ? 'border-red-300 text-red-700 hover:bg-red-50'
                : 'border-slate-300 text-slate-700 hover:bg-slate-50',
            )}
          >
            {fixMutation.isPending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : check.fix.kind === 'revoke' ? (
              <Ban size={13} />
            ) : (
              <Wrench size={13} />
            )}
            {check.fix.label}
          </button>
        )}
      </div>

      {result && (
        <div className="mt-2 space-y-1.5 border-t border-slate-200/70 pt-2">
          <div
            className={clsx(
              'flex items-start gap-1.5 text-xs',
              result.ok ? 'text-emerald-700' : 'text-red-700',
            )}
          >
            {result.ok ? (
              <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
            ) : (
              <XCircle size={13} className="mt-0.5 shrink-0" />
            )}
            <span>{result.ok ? 'Erledigt, wird neu geprüft…' : result.error}</span>
          </div>
          {result.log && (
            <pre className="max-h-40 overflow-auto rounded bg-konsole px-2.5 py-1.5 font-mono text-[11px] leading-relaxed text-konsole-hell">
              {result.log}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

export default function HostCheckPanel({
  serverId,
  hostname,
  onClose,
}: {
  serverId: string;
  hostname: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { relative } = useDateFormat();

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['server-host-check', serverId],
    queryFn: () => servers.hostCheck(serverId),
    // An SSH login per call, so no polling; long enough that reopening the panel
    // does not reconnect.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const message =
    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    (error instanceof Error ? error.message : '');

  // Groups in the order the service emits them, so the reading order matches the
  // dependency order: no SSH, no Docker answer; no Docker, nothing else matters.
  const groups = data
    ? [...new Set(data.checks.map((c) => c.group))].map((group) => ({
        group,
        checks: data.checks.filter((c) => c.group === group),
      }))
    : [];

  const blockingFails =
    data?.checks.filter((c) => c.blocking && c.status === 'FAIL').length ?? 0;
  const otherFails = (data?.summary.fail ?? 0) - blockingFails;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4">
      <div className="mt-8 flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{t('servers.hostCheck')}</h2>
            <p className="font-mono text-xs text-slate-500">{hostname}</p>
          </div>
          <div className="flex items-center gap-2">
            {data && (
              <span className="text-xs text-slate-400">
                Stand: {relative(data.collectedAt)} · {(data.durationMs / 1000).toFixed(1)} s
              </span>
            )}
            <button
              onClick={() => void refetch()}
              disabled={isFetching}
              className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {isFetching ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <RefreshCw size={13} />
              )}
              Erneut prüfen
            </button>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto bg-slate-50 px-6 py-5">
          {isLoading && (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
              <Loader2 size={16} className="animate-spin" />
              Verbinde per SSH und prüfe den Host…
            </div>
          )}

          {isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
                <div>
                  <div className="text-sm font-medium text-red-800">{t('servers.checkNotPossible')}</div>
                  <p className="mt-1 text-sm text-red-700">{message}</p>
                </div>
              </div>
            </div>
          )}

          {data && (
            <>
              <div
                className={clsx(
                  'flex items-start gap-3 rounded-lg border p-4',
                  data.ok
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-red-200 bg-red-50',
                )}
              >
                {data.ok ? (
                  <ShieldCheck size={20} className="mt-0.5 shrink-0 text-emerald-600" />
                ) : (
                  <ShieldAlert size={20} className="mt-0.5 shrink-0 text-red-600" />
                )}
                <div>
                  <div
                    className={clsx(
                      'text-sm font-semibold',
                      data.ok ? 'text-emerald-900' : 'text-red-900',
                    )}
                  >
                    {/* `ok` counts only blocking failures, so a red row can sit
                        under a green heading — "zu wenig RAM" stops nothing from
                        being deployed but is not a pass either. The headline says
                        which of the two it is. */}
                    {!data.ok
                      ? `${blockingFails === 1 ? 'Eine harte Bedingung ist' : `${blockingFails} harte Bedingungen sind`} nicht erfüllt — hier lässt sich nichts ausrollen`
                      : otherFails > 0
                        ? 'Ausrollen ist möglich, aber der Host hat rote Punkte'
                        : data.summary.warn > 0
                          ? 'Alle harten Bedingungen erfüllt, mit Hinweisen'
                          : 'Der Host erfüllt alle Bedingungen'}
                  </div>
                  <p
                    className={clsx(
                      'mt-0.5 text-xs',
                      data.ok ? 'text-emerald-800' : 'text-red-800',
                    )}
                  >
                    {data.summary.pass} bestanden
                    {data.summary.warn > 0 && `, ${data.summary.warn} mit Hinweis`}
                    {data.summary.fail > 0 && `, ${data.summary.fail} durchgefallen`}
                    {data.summary.unknown > 0 && `, ${data.summary.unknown} unklar`}
                    {data.ok && (data.summary.warn > 0 || otherFails > 0)
                      ? ' — nichts davon verhindert ein Deploy, aber genau das sind die Gründe, warum es später schiefgeht.'
                      : ''}
                  </p>
                </div>
              </div>

              {data.warnings.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-amber-900">
                    <AlertTriangle size={13} /> Teilweise unvollständig
                  </div>
                  <ul className="mt-1 space-y-0.5 text-xs text-amber-800">
                    {data.warnings.map((w) => (
                      <li key={w}>• {w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {groups.map(({ group, checks }) => (
                <div key={group}>
                  <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {group}
                  </h3>
                  <div className="space-y-1.5">
                    {checks.map((check) => (
                      <CheckRow
                        key={check.key}
                        check={check}
                        serverId={serverId}
                        onFixed={() => void refetch()}
                      />
                    ))}
                  </div>
                </div>
              ))}

              <p className="text-[11px] leading-snug text-slate-400">
                Das Prüfen selbst ist lesend, mit einer Ausnahme: für die Schreibprüfung wird
                unter /opt/juni ein leeres Verzeichnis angelegt und wieder entfernt. Es wird
                nichts gezogen, gestartet oder geändert. Bei der Registry wird geprüft, ob sie
                erreichbar ist und ob überhaupt eine Anmeldung hinterlegt ist — nicht, ob das
                Passwort noch gilt. Portkonflikte je Stack klärt der Pre-flight beim Ausrollen.
                {data.ownership === 'CUSTOMER_PROVIDED' && (
                  <>
                    {' '}
                    Dieser Server gehört dem Kunden — Maßnahmen ändern eine Maschine, die nicht
                    uns gehört, und werden protokolliert.
                  </>
                )}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
